/**
 * Klantportaal / recruiter: één thread op `applications.cv_snapshot`
 * (velden portal_thread, portal_client_proposal, legacy portal_client_feedback).
 */

export type PortalThreadAuthor = 'recruiter' | 'client';

export interface PortalThreadEntry {
  at: string;
  author: PortalThreadAuthor;
  text: string;
}

export interface PortalClientProposalSnapshot {
  summary?: string;
  available_from?: string;
  hours_per_week?: string;
  salary_indication?: string;
  match_notes?: string;
  /** Optioneel: laatste door recruiter gekozen matchscore (fallback als kolom leeg). */
  match_score?: number;
  published_at?: string;
  vacancy_id?: string;
}

function safeIsoAt(raw: unknown): string {
  const s = String(raw || '').trim();
  if (!s) return new Date().toISOString();
  const t = Date.parse(s);
  return Number.isNaN(t) ? new Date().toISOString() : new Date(t).toISOString();
}

/**
 * Sommige paden leveren `cv_snapshot` als JSON-string; PostgREST meestal als object.
 * Eén genormaliseerd object voor portal_thread / portal_client_proposal.
 */
export function normalizeCvSnapshotForPortal(cv_snapshot: unknown): Record<string, unknown> {
  let raw: unknown = cv_snapshot;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return {};
    try {
      raw = JSON.parse(s) as unknown;
    } catch {
      return {};
    }
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
  return {};
}

/** Lees chronologische thread; migreert legacy `portal_client_feedback` als er nog geen portal_thread is. */
export function parsePortalThreadFromCvSnapshot(cv_snapshot: unknown): PortalThreadEntry[] {
  const snap = normalizeCvSnapshotForPortal(cv_snapshot);
  const rawThread = snap.portal_thread;
  const fromThread: PortalThreadEntry[] = Array.isArray(rawThread)
    ? (rawThread as unknown[])
        .map((x: any) => ({
          at: safeIsoAt(x?.at),
          author: x?.author === 'recruiter' || x?.author === 'client' ? (x.author as PortalThreadAuthor) : 'client',
          text: String(x?.text || '').trim(),
        }))
        .filter((x) => x.text.length > 0)
    : [];

  if (fromThread.length > 0) {
    return [...fromThread].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  }

  const legacy = snap.portal_client_feedback;
  const fromLegacy: PortalThreadEntry[] = Array.isArray(legacy)
    ? (legacy as unknown[])
        .map((x: any) => ({
          at: safeIsoAt(x?.at),
          author: 'client' as const,
          text: String(x?.text || '').trim(),
        }))
        .filter((x) => x.text.length > 0)
    : [];

  return [...fromLegacy].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
}

export function parsePortalClientProposal(cv_snapshot: unknown): PortalClientProposalSnapshot | null {
  const snap = normalizeCvSnapshotForPortal(cv_snapshot);
  const p = snap.portal_client_proposal;
  if (!p || typeof p !== 'object' || Array.isArray(p)) return null;
  const o = p as Record<string, unknown>;
  return {
    summary: o.summary != null ? String(o.summary) : undefined,
    available_from: o.available_from != null ? String(o.available_from) : undefined,
    hours_per_week: o.hours_per_week != null ? String(o.hours_per_week) : undefined,
    salary_indication: o.salary_indication != null ? String(o.salary_indication) : undefined,
    match_notes: o.match_notes != null ? String(o.match_notes) : undefined,
    match_score:
      o.match_score != null && !Number.isNaN(Number(o.match_score))
        ? Math.max(0, Math.min(100, Math.round(Number(o.match_score))))
        : undefined,
    published_at: o.published_at != null ? String(o.published_at) : undefined,
    vacancy_id: o.vacancy_id != null ? String(o.vacancy_id) : undefined,
  };
}

export function hasUnreadClientPortalActivity(cv_snapshot: unknown, opts?: { afterIso?: string | null }): boolean {
  const thread = parsePortalThreadFromCvSnapshot(cv_snapshot);
  if (!thread.length) return false;
  const last = thread[thread.length - 1];
  if (last.author !== 'client') return false;
  const after = opts?.afterIso ? Date.parse(opts.afterIso) : 0;
  if (!after || Number.isNaN(after)) return true;
  return Date.parse(last.at) > after;
}

/** Payload vanuit CandidateMoveModal bij status «Voorgesteld». */
export interface VoorgesteldClientPayload {
  publishToPortal: boolean;
  emailToClient: boolean;
  /** Primaire ontvanger (één adres); extra adressen via clientEmailCc. */
  clientEmailTo: string;
  clientEmailCc: string;
  clientEmailBcc: string;
  clientEmailSubject: string;
  clientEmailBodyHtml: string;
  /** Zichtbaar voor recruiters op vacature (notities) en kandidaat-tijdlijn (bedrijf). */
  clientProposalInternalNotes: string;
  /** Optioneel: opgeslagen CV Builder-opmaak voor klantportaal (design/pages). */
  portalCvSnapshot: Record<string, unknown> | null;
  recruiterSummary: string;
  availableFrom: string;
  hoursPerWeek: string;
  salaryIndication: string;
  matchNotes: string;
  /** Matchscore 0–100 voor `applications.match_score` / klantportaal-kaart na publiceren. */
  portalMatchScore: number;
}

/** Eerste regel in kandidaatnotitie — voorkomt dubbele notities bij opnieuw publiceren. */
export function voorgesteldCandidateNoteMarker(applicationId: string): string {
  return `wt-app-note:${String(applicationId || '').trim()}`;
}

/** Tekst voor `candidate_notes` na publicatie naar klantportaal (zelfde inhoud als portaal-voorstel). */
export function buildVoorgesteldCandidateNoteContent(
  applicationId: string,
  vacancyTitle: string,
  payload: VoorgesteldClientPayload,
): string | null {
  const summary = String(payload.recruiterSummary || '').trim();
  const matchNotes = String(payload.matchNotes || '').trim();
  const internal = String(payload.clientProposalInternalNotes || '').trim();
  const availableFrom = String(payload.availableFrom || '').trim();
  const hours = String(payload.hoursPerWeek || '').trim();
  const salary = String(payload.salaryIndication || '').trim();
  if (!summary && !matchNotes && !internal && !availableFrom && !hours && !salary) return null;

  const lines: string[] = [
    voorgesteldCandidateNoteMarker(applicationId),
    `[Voorgesteld — ${vacancyTitle || 'vacature'}]`,
    '',
  ];
  if (summary) {
    lines.push('Feedback voor klant (portaal):', summary, '');
  }
  if (matchNotes) {
    lines.push('Waarom match bij vacature:', matchNotes, '');
  }
  const logistics = [
    availableFrom && `Beschikbaar vanaf: ${availableFrom}`,
    hours && `Uren per week: ${hours}`,
    salary && `Salarisindicatie: ${salary}`,
  ].filter(Boolean);
  if (logistics.length) {
    lines.push(...logistics, '');
  }
  if (internal) {
    lines.push('[Intern — niet zichtbaar in klantportaal]', internal);
  }
  return lines.join('\n').trim();
}

/** Verberg technische markerregel in de UI. */
export function formatVoorgesteldCandidateNoteForDisplay(content: string): string {
  return String(content || '')
    .replace(/^wt-app-note:[^\n]+\n?/, '')
    .trim();
}
